import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { Reveal, Eyebrow } from "@/components/landing/Reveal";
import { PatternLayer, patternLight } from "@/components/landing/patterns";
import { useSEO } from "@/lib/seo";
import { SITE } from "@/lib/site";

const Section = ({ heading, children, testid }) => (
  <section className="mt-12" data-testid={testid}>
    <h2 className="font-display text-xl md:text-2xl text-neutral-900">{heading}</h2>
    <div className="mt-4 space-y-3 text-sm md:text-base text-neutral-600 leading-relaxed">{children}</div>
  </section>
);

// Only verified, existing Gold Time policies appear on this page. Anything
// not yet confirmed by the owner is intentionally left out (tracked as
// OWNER POLICY NEEDED in the P1 report — never invented here).
export default function WarrantyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useSEO({
    title: "Warranty & Authenticity | Gold Time Jewelry",
    canonicalPath: "/warranty",
    description:
      "How Gold Time Jewelry stands behind its jewelry, diamonds, pre-owned watches, custom pieces and repairs — family-owned Texas jeweler since 1991.",
  });

  return (
    <main data-testid="warranty-page">
      <Header />
      <section className="relative overflow-hidden pt-[164px] pb-24 lg:pt-[196px] lg:pb-32">
        <PatternLayer pattern={patternLight} />
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <Eyebrow>Shop With Confidence</Eyebrow>
            <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight text-neutral-900" data-testid="warranty-heading">
              Warranty &amp; <span className="text-[#C9A227]">authenticity</span>
            </h1>
            <p className="text-sm md:text-base text-neutral-600 leading-relaxed mt-6">
              Gold Time Jewelry has served Texas since 1991. Every piece we sell is genuine and exactly as
              described — karat, stone type and creation method are stated plainly on each product page,
              including clear lab-grown diamond labeling.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <Section heading="Jewelry" testid="warranty-jewelry">
              <p>
                Unworn items in original condition may be returned within{" "}
                <strong className="text-neutral-800">30 days</strong> of delivery or pickup for a full refund
                to your original payment method. Every order ships fully insured, or can be shipped to a Gold
                Time showroom for pickup. Details live in our{" "}
                <Link to="/refund-policy" className="text-[#C9A227] font-semibold hover:text-[#A8871B]">refund policy</Link>.
              </p>
            </Section>

            <Section heading="Diamonds" testid="warranty-diamonds">
              <p>
                Natural and lab-grown diamonds are always labeled as such — never mixed, never ambiguous.
                Stone type, carat weight, color and clarity are listed on the product page whenever those
                details are specified for the piece.
              </p>
            </Section>

            <Section heading="Pre-owned watches" testid="warranty-watches">
              <p>
                Pre-owned watches are described honestly, including condition. Because each pre-owned piece is
                one of a kind, please ask our team about the specific watch you&rsquo;re considering before
                purchasing — call any showroom and we&rsquo;ll walk you through it.
              </p>
            </Section>

            <Section heading="Custom jewelry &amp; name plates" testid="warranty-custom">
              <p>
                Custom designs, name plates, engraved pieces and rings sized to your requested size are made
                for you and are <strong className="text-neutral-800">final sale</strong>. We confirm details
                with you before work begins so there are no surprises.
              </p>
            </Section>

            <Section heading="Repairs" testid="warranty-repairs">
              <p>
                Our showrooms handle jewelry and watch repair in person — bring the piece by Katy, Copperas
                Cove or Killeen and we&rsquo;ll assess it with you before any work is quoted.
              </p>
            </Section>

            <div className="mt-14 bg-white border border-[#E8DCC4] rounded-sm p-6" data-testid="warranty-contact">
              <h2 className="font-display text-lg text-neutral-900">A question about a specific piece?</h2>
              <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
                Call or visit any showroom — Katy {SITE.locations[0].phone} · Copperas Cove{" "}
                {SITE.locations[1].phone} · Killeen {SITE.locations[2].phone} — or{" "}
                <Link to="/contact" className="text-[#C9A227] font-semibold hover:text-[#A8871B]">
                  send us a note
                </Link>
                .
              </p>
            </div>
          </Reveal>
        </div>
      </section>
      <Footer />
    </main>
  );
}
